import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { KeyRound, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { FAQ_IMAGE } from "@/data/content";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AtelierLogin() {
  const { status, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (status === "authenticated") {
    const from = location.state?.from?.pathname || "/atelier";
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { mustChangePassword } = await login(email.trim(), password);
      const dest = mustChangePassword
        ? "/atelier/change-password"
        : (location.state?.from?.pathname || "/atelier");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message || "Unable to enter the atelier.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="atelier-login" className="relative min-h-screen bg-obsidian flex items-center justify-center overflow-hidden">
      <img src={FAQ_IMAGE} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-b from-obsidian/85 via-obsidian/90 to-obsidian" />
      <div
        className="absolute inset-0 pointer-events-none opacity-40 mix-blend-overlay"
        style={{ background: "radial-gradient(ellipse at 50% 40%, rgba(183,166,200,0.35), transparent 60%)" }}
      />

      <div className="relative z-10 w-full max-w-md px-6 md:px-0">
        <Link to="/" className="label-eyebrow text-lavender hover:text-bone transition-colors inline-flex items-center gap-2" data-testid="atelier-login-back">
          <ArrowUpRight size={12} className="rotate-180" /> Back to the House
        </Link>

        <div className="mt-8 flex items-center gap-3">
          <KeyRound className="text-lavender" size={20} />
          <p className="label-eyebrow text-bone/60">Private Entrance</p>
        </div>

        <h1 className="mt-4 font-display text-bone text-4xl md:text-5xl leading-[1.05]">
          The Curator&apos;s <em className="not-italic text-lavender">Atelier</em>
        </h1>
        <p className="mt-4 font-display italic text-bone/70 text-lg leading-snug max-w-sm">
          A quiet room behind the House. Only the curator holds the key.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="atelier-login-form">
          <div>
            <label className="label-eyebrow text-bone/60">Email</label>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="atelier-login-email"
              className="mt-2 w-full bg-transparent border-b border-bone/20 focus:border-lavender py-3 text-bone placeholder-bone/30 outline-none transition-colors"
              placeholder="you@theuncannyhouse.com"
            />
          </div>
          <div>
            <label className="label-eyebrow text-bone/60">Password</label>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="atelier-login-password"
              className="mt-2 w-full bg-transparent border-b border-bone/20 focus:border-lavender py-3 text-bone placeholder-bone/30 outline-none transition-colors"
              placeholder="••••••••••••"
            />
          </div>

          {error && (
            <p data-testid="atelier-login-error" className="text-sm text-lavender">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="atelier-login-submit"
            className="w-full inline-flex items-center justify-center px-8 py-4 bg-lavender text-obsidian text-[12px] uppercase tracking-[0.3em] hover:bg-bone transition-colors disabled:opacity-60"
          >
            {submitting ? "Unlocking…" : "Enter the Atelier"}
          </button>
        </form>

        <p className="mt-8 text-[11px] text-bone/40 leading-relaxed">
          Unauthorized access is prohibited. All login attempts are recorded.
        </p>
      </div>
    </div>
  );
}
